<template>
  <div class="overlay">
    <div class="modal">
      <h2>{{ esEdicion ? "Editar paciente" : "Nuevo paciente" }}</h2>

      <div class="campo">
        <label for="np-nombre">Nombre</label>
        <!-- autocomplete="off": son datos del paciente, no deben quedar en el autofill. -->
        <input v-model="nombre" id="np-nombre" name="np-nombre" autocomplete="off" placeholder="Nombre" />
      </div>

      <div class="campo">
        <label for="np-apellido">Apellido</label>
        <input v-model="apellido" id="np-apellido" name="np-apellido" autocomplete="off" placeholder="Apellido" />
      </div>

      <!-- Aviso nombre duplicado: visible mientras se escribe -->
      <div v-if="hayDuplicadoNombre" class="alerta-nombre-duplicado">
        Ya existe un paciente con ese nombre. Ingresá el número de documento para distinguirlos.
      </div>

      <!-- CI / RUT: siempre visible -->
      <div class="campo">
        <label for="np-ci">CI / RUT</label>
        <input v-model="ci" id="np-ci" name="np-ci" autocomplete="off" placeholder="Documento" />
        <div v-if="hayDuplicadoCI" class="alerta-duplicado">
          Ya hay un paciente registrado con ese número de documento.
        </div>
      </div>

      <div class="acciones">
        <!-- :disabled mientras guarda: sin esto, un doble toque (fácil en celular con la red lenta)
             creaba el paciente DOS veces. -->
        <button @click="guardar" :disabled="guardando">
          {{ guardando ? "Guardando…" : "Guardar" }}
        </button>
        <button @click="emit('cerrar')" :disabled="guardando">Cancelar</button>
      </div>
    </div>
  </div>

  <!-- Modal de pago pendiente -->
  <ModalPagoPendiente
    :mostrar="mostrarModalPago"
    @cerrar="cerrarModalPago"
  />
  <!-- Los avisos ahora salen por el diálogo global (ConfirmDialog en App.vue). -->
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { usePacientesStore } from "@/stores/pacientes";
import ModalPagoPendiente from "@/components/ModalPagoPendiente.vue";
import { usePagoVerificacion } from "@/composables/usePagoVerificacion";
import { marcarEdicion } from "@/composables/edicionPendiente";
import { separarNombreApellido } from "@/utils/nombrePaciente";
import { useDialog } from "@/composables/useDialog";

const emit = defineEmits(["cerrar"]);
const props = defineProps({
  paciente: Object,
});

const store = usePacientesStore();
const { mostrarModalPago, verificarPermisoPago, cerrarModalPago } = usePagoVerificacion();
const { alerta } = useDialog();

const nombre = ref("");
const apellido = ref("");
const ci = ref("");
const guardando = ref(false);

const esEdicion = computed(() => !!props.paciente);

// Mientras el modal de paciente esté abierto, no dejar que la auto-actualización
// recargue la app y borre lo que se está cargando.
onMounted(() => marcarEdicion("paciente-nuevo", true));
onUnmounted(() => marcarEdicion("paciente-nuevo", false));

// Detecta si ya existe un paciente con el mismo CI/RUT
const hayDuplicadoCI = computed(() => {
  if (guardando.value) return false;
  const doc = ci.value.trim();
  if (!doc) return false;
  return store.pacientes.some((p) => {
    if (esEdicion.value && props.paciente && p.id === props.paciente.id) return false;
    return p.ci && p.ci.trim() === doc;
  });
});

// Detecta si ya existe un paciente con el mismo nombre completo
const hayDuplicadoNombre = computed(() => {
  if (guardando.value) return false;
  const n = nombre.value.trim();
  const a = apellido.value.trim();
  if (!n || !a) return false;
  const nombreCompleto = `${n} ${a}`.toLowerCase();
  return store.pacientes.some((p) => {
    // Al editar, excluir al paciente actual
    if (esEdicion.value && props.paciente && p.id === props.paciente.id) return false;
    return p.nombre.toLowerCase() === nombreCompleto;
  });
});

// cuando entra un paciente, precarga los datos
watch(
  () => props.paciente,
  (p) => {
    if (p) {
      // Con apellido persistido se recupera exacto; sin él (pacientes viejos),
      // el helper cae al split por último espacio de siempre.
      const sep = separarNombreApellido(p.nombre, p.apellido);
      nombre.value = sep.nombre;
      apellido.value = sep.apellido;
      ci.value = p.ci || "";
    } else {
      nombre.value = "";
      apellido.value = "";
      ci.value = "";
    }
  },
  { immediate: true },
);

async function guardar() {
  // Verificar permiso de pago primero
  if (!verificarPermisoPago()) {
    return;
  }

  if (!nombre.value.trim()) {
    alerta("Por favor, ingrese el nombre del paciente.");
    return;
  }

  if (!apellido.value.trim()) {
    alerta("Por favor, ingrese el apellido del paciente.");
    return;
  }

  if (hayDuplicadoCI.value) {
    alerta("Ya hay un paciente registrado con ese número de documento.");
    return;
  }

  if (hayDuplicadoNombre.value && !ci.value.trim()) {
    return; // El aviso inline ya indica que debe ingresar el documento
  }

  const apellidoTrim = apellido.value.trim();
  const nombreCompleto = `${nombre.value.trim()} ${apellidoTrim}`.trim();

  try {
    guardando.value = true;
    if (esEdicion.value) {
      store.editarPaciente(props.paciente.id, { nombre: nombreCompleto, apellido: apellidoTrim, ci: ci.value.trim() });
    } else {
      await store.agregarPaciente({ nombre: nombreCompleto, apellido: apellidoTrim, ci: ci.value.trim() });
    }
    emit("cerrar");
  } catch (e) {
    guardando.value = false;
    alerta(e.message || "No se pudo guardar el paciente. Intente nuevamente.");
  }
}
</script>

<style scoped>
/* Fondo semitransparente detrás del modal */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

/* Caja del modal */
.modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  padding: 20px;
  width: 100%;
  max-width: 400px;
  font-family: system-ui, sans-serif;

  display: flex;
  flex-direction: column;
  align-items: stretch;

  /* Nunca más alto que la pantalla, y si no entra se desliza dentro del modal.
     Sin esto, en iPhone (teclado abierto) el botón Guardar quedaba fuera de la vista.
     dvh tiene en cuenta la barra dinámica de Safari; vh es el respaldo para navegadores viejos. */
  max-height: 90vh;
  max-height: 90dvh;
  overflow-y: auto;
}

.modal h2 {
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: #678AB2;
  text-align: center;
}

.modal input {
  width: 100%;
  padding: 10px;
  margin: 6px 0;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
}

.modal input:focus {
  outline: none;
  border-color: #678AB2;
  box-shadow: 0 0 4px rgba(103, 138, 178, 0.4);
}

.acciones {
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
}

.acciones button {
  flex: 1;
  margin: 0 4px;
  padding: 10px 0;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.acciones button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.acciones button:first-of-type {
  background: #678AB2;
  color: #fff;
}

.acciones button:first-of-type:hover {
  background: #4d6f96;
}

.acciones button:last-of-type {
  background: #eee;
  color: #333;
}

.acciones button:last-of-type:hover {
  background: #ddd;
}

.campo {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
}

.campo label {
  font-size: 13px;
  color: #444;
}

.label-opcional {
  font-size: 11px;
  color: #f59e0b;
  font-weight: 600;
  margin-left: 4px;
}

/* Alerta de duplicado */
.alerta-duplicado {
  background: #fff8e1;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #92400e;
  margin-bottom: 8px;
  line-height: 1.4;
}

/* Aviso nombre duplicado (más suave, informativo) */
.alerta-nombre-duplicado {
  background: #fffbeb;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #92400e;
  margin-bottom: 8px;
  line-height: 1.4;
}
</style>
